export interface DateRangeInterval {
  from: Date;
  to: Date;
}

export enum Semester {
  FIRST = 'first',
  SECOND = 'second',
}

export interface SchoolTermDateScope {
  schoolTermSemester: `${Semester}`;
  startDate: string | Date;
  endDate: string | Date;
}

export interface SchoolTermDateBounds {
  from?: string;
  to?: string;
}

export const now = (): Date => new Date();

export const nowMs = (): number => now().getTime();

export const toIsoDateTimeString = (value: string | Date): string => new Date(value).toISOString();

export const toIsoDateString = (value: string | Date): string => new Date(value).toISOString().slice(0, 10);

export function toDateOrNull(unixSeconds?: number | null): Date | null {
  return unixSeconds ? new Date(unixSeconds * 1000) : null;
}

/** Returns the later of two dates, ignoring nulls (null if both are null). */
export function maxDate(a: Date | null, b: Date | null): Date | null {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

export function getMinutesUntil(dueDate: Date): number {
  const diffMs = dueDate.getTime() - nowMs();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60)));
}

const normalizeDateToDay = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

export function getSchoolTermForInterval(
  toInspect: DateRangeInterval,
  firstSemester: DateRangeInterval,
  secondSemester: DateRangeInterval,
): Semester | null {
  if (firstSemester.to >= secondSemester.to) {
    throw new Error('First semester must end before second semester');
  }

  const overlapsFirst = intervalOverlaps(toInspect, firstSemester);
  const overlapsSecond = intervalOverlaps(toInspect, secondSemester);
  const insideFirst = intervalContains(firstSemester, toInspect);
  const insideSecond = intervalContains(secondSemester, toInspect);

  const hitsFirst = overlapsFirst || insideFirst;
  const hitsSecond = overlapsSecond || insideSecond;

  if (hitsFirst && hitsSecond) {
    return null;
  }

  if (hitsFirst) {
    return Semester.FIRST;
  }

  if (hitsSecond) {
    return Semester.SECOND;
  }

  return null;
}

export const intervalOverlaps = (left: DateRangeInterval, right: DateRangeInterval): boolean => {
  const leftFrom = normalizeDateToDay(left.from);
  const leftTo = normalizeDateToDay(left.to);
  const rightFrom = normalizeDateToDay(right.from);
  const rightTo = normalizeDateToDay(right.to);

  return leftFrom <= rightTo && leftTo >= rightFrom;
};

export const intervalContains = (outer: DateRangeInterval, inner: DateRangeInterval): boolean => {
  const outerFrom = normalizeDateToDay(outer.from);
  const outerTo = normalizeDateToDay(outer.to);
  const innerFrom = normalizeDateToDay(inner.from);
  const innerTo = normalizeDateToDay(inner.to);

  return innerFrom >= outerFrom && innerTo <= outerTo;
};

// A bare YYYY-MM-DD must be read as that LOCAL calendar day; native `new Date('2026-07-22')`
// parses it as UTC midnight and can shift the day. Full timestamps parse natively as-is.
const parseLocalDate = (value: string | Date): Date => {
  if (value instanceof Date) return new Date(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
};

export const normalizeToStartOfDay = (value: string | Date): string => {
  const date = parseLocalDate(value);
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

export const normalizeToEndOfDay = (value: string | Date): string => {
  const date = parseLocalDate(value);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

/**
 * Resolves the app-wide history scope represented by a school-term selection.
 * The first semester owns all class history through its inclusive end; the
 * second owns all class history from its inclusive start. No term means all
 * class history, so neither bound is applied.
 */
export const getSchoolTermDateBounds = (schoolTerm?: SchoolTermDateScope | null): SchoolTermDateBounds => {
  if (!schoolTerm) return {};

  if (schoolTerm.schoolTermSemester === Semester.FIRST) {
    return { to: normalizeToEndOfDay(schoolTerm.endDate) };
  }

  return { from: normalizeToStartOfDay(schoolTerm.startDate) };
};

/**
 * Checks whether a date belongs to the app-wide history scope represented by a
 * term selection. Bounds are inclusive; no term means unbounded class history.
 */
export const belongsToSchoolTerm = (params: { val: string | Date; term: SchoolTermDateScope | null }): boolean => {
  const date = new Date(params.val).getTime();
  if (Number.isNaN(date)) return false;

  const { from, to } = getSchoolTermDateBounds(params.term);
  return (!from || date >= new Date(from).getTime()) && (!to || date <= new Date(to).getTime());
};

export const getDaysUntilDate = (date: Date): number => {
  const normalizedNow = normalizeDateToDay(now());
  const normalizedDate = normalizeDateToDay(date);

  if (normalizedDate < normalizedNow) {
    return 0;
  }

  const diffTime = normalizedDate.getTime() - normalizedNow.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
